import { useState } from "react";
import { EU_COUNTRIES, findCountry, getApplicableRate } from "../data/vatData";
import { fmtEUR, fmtPct } from "../lib/format";
import { useI18n } from "../i18n/I18nContext";
import { Input } from "./ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "./ui/select";
import { Plus, Trash2, FileSpreadsheet, BadgeCheck, FileDown, FileText } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { trackExport } from "../lib/analytics";

const newRow = () => ({
    id: typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2),
    country: "DE",
    amount: "",
});

function csvEscape(v) {
    const s = String(v ?? "");
    return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadBlob(filename, content, mime) {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function QuarterlyEstimator() {
    const { t } = useI18n();
    const [rows, setRows] = useState([
        { id: "r1", country: "DE", amount: "1500" },
        { id: "r2", country: "FR", amount: "850" },
        { id: "r3", country: "IT", amount: "400" },
    ]);

    const update = (id, patch) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    const remove = (id) => setRows((rs) => rs.filter((r) => r.id !== id));
    const add = () => setRows((rs) => (rs.length >= 10 ? rs : [...rs, newRow()]));

    const computed = rows.map((r) => {
        const c = findCountry(r.country);
        const { rate } = getApplicableRate(c, "standard");
        const amt = parseFloat(r.amount) || 0;
        const vat = +(amt * (rate / 100)).toFixed(2);
        return { ...r, country: c, rate, amt, vat };
    });
    const totalSales = computed.reduce((s, r) => s + r.amt, 0);
    const totalVat = computed.reduce((s, r) => s + r.vat, 0);

    // ---------- Exports ----------
    const exportCSV = () => {
        const headers = [t("estimator.country"), "ISO", t("estimator.sales"), t("estimator.vatRate"), t("estimator.vatOwed")];
        const lines = [headers.map(csvEscape).join(",")];
        computed.forEach((r) => {
            lines.push(
                [r.country.name, r.country.code, r.amt.toFixed(2), r.rate.toFixed(2) + "%", r.vat.toFixed(2)]
                    .map(csvEscape)
                    .join(",")
            );
        });
        lines.push("");
        lines.push([t("estimator.totalSales"), "", totalSales.toFixed(2), "", ""].map(csvEscape).join(","));
        lines.push([t("estimator.totalVat"), "", "", "", totalVat.toFixed(2)].map(csvEscape).join(","));
        downloadBlob(`vatcalc-quarterly-${new Date().toISOString().slice(0, 10)}.csv`, lines.join("\n"), "text/csv;charset=utf-8");
        trackExport("csv");
        toast.success(t("estimator.csvExported"));
    };

    const exportPDF = async () => {
        try {
            const [{ default: jsPDF }, autoTableMod] = await Promise.all([
                import("jspdf"),
                import("jspdf-autotable"),
            ]);
            const autoTable = autoTableMod.default || autoTableMod;

            const doc = new jsPDF({ unit: "pt", format: "a4" });
            doc.setFont("helvetica", "bold");
            doc.setFontSize(20);
            doc.setTextColor(15, 23, 42); // slate-900
            doc.text(t("estimator.pdfTitle"), 40, 50);

            doc.setFont("helvetica", "normal");
            doc.setFontSize(10);
            doc.setTextColor(100, 116, 139); // slate-500
            doc.text(`${t("estimator.pdfGenerated")}: ${new Date().toLocaleDateString()}`, 40, 70);

            autoTable(doc, {
                startY: 90,
                head: [[
                    t("estimator.country"),
                    t("estimator.sales"),
                    t("estimator.vatRate"),
                    t("estimator.vatOwed"),
                ]],
                body: computed.map((r) => [
                    `${r.country.name} (${r.country.code})`,
                    `€ ${r.amt.toFixed(2)}`,
                    `${r.rate.toFixed(2)}%`,
                    `€ ${r.vat.toFixed(2)}`,
                ]),
                foot: [[
                    t("estimator.totalSales"),
                    `€ ${totalSales.toFixed(2)}`,
                    "",
                    `€ ${totalVat.toFixed(2)}`,
                ]],
                theme: "grid",
                headStyles: { fillColor: [15, 23, 42], textColor: 255, fontStyle: "bold" },
                footStyles: { fillColor: [16, 185, 129], textColor: 255, fontStyle: "bold" },
                styles: { font: "helvetica", fontSize: 10, cellPadding: 8 },
                columnStyles: {
                    1: { halign: "right" },
                    2: { halign: "right" },
                    3: { halign: "right" },
                },
            });

            const finalY = (doc.lastAutoTable && doc.lastAutoTable.finalY) || 200;
            doc.setFontSize(9);
            doc.setTextColor(100, 116, 139);
            doc.text("VATcalc.eu — vatcalc.eu", 40, finalY + 30);

            doc.save(`vatcalc-quarterly-${new Date().toISOString().slice(0, 10)}.pdf`);
            trackExport("pdf");
            toast.success(t("estimator.pdfExported"));
        } catch (e) {
            toast.error(t("estimator.pdfError"));
        }
    };

    return (
        <section data-testid="quarterly-estimator-section" className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                <div className="lg:col-span-4">
                    <div className="text-xs font-semibold tracking-[0.2em] uppercase text-slate-500">
                        {t("estimator.eyebrow")}
                    </div>
                    <h2 className="font-display mt-3 text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-slate-900">
                        {t("estimator.titleLine1")}
                        <br className="hidden sm:block" />
                        {t("estimator.titleLine2")}
                    </h2>
                    <p className="mt-3 text-slate-600 leading-relaxed">{t("estimator.intro")}</p>
                </div>

                <div data-testid="estimator-card" className="lg:col-span-8 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                        <div className="flex items-center gap-2">
                            <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-slate-100 text-slate-700">
                                <FileSpreadsheet className="h-4 w-4" />
                            </span>
                            <h3 className="font-display text-lg font-bold text-slate-900">
                                {t("estimator.salesRows")}
                            </h3>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <Button
                                data-testid="export-csv-btn"
                                onClick={exportCSV}
                                variant="outline"
                                size="sm"
                                className="border-slate-200 text-slate-700 hover:bg-slate-100"
                            >
                                <FileDown className="h-4 w-4 mr-1.5" />
                                {t("estimator.exportCsv")}
                            </Button>
                            <Button
                                data-testid="export-pdf-btn"
                                onClick={exportPDF}
                                variant="outline"
                                size="sm"
                                className="border-slate-200 text-slate-700 hover:bg-slate-100"
                            >
                                <FileText className="h-4 w-4 mr-1.5" />
                                {t("estimator.exportPdf")}
                            </Button>
                            <Button
                                data-testid="add-row-btn"
                                onClick={add}
                                disabled={rows.length >= 10}
                                size="sm"
                                className="bg-[#10B981] hover:bg-emerald-600 text-white"
                            >
                                <Plus className="h-4 w-4 mr-1" />
                                {t("estimator.addRow")} {rows.length >= 10 ? `(${t("estimator.max")})` : `(${rows.length}/10)`}
                            </Button>
                        </div>
                    </div>

                    <div className="overflow-x-auto -mx-2">
                        <table className="w-full min-w-[560px] text-sm" data-testid="estimator-table">
                            <thead>
                                <tr className="text-left text-xs font-semibold tracking-[0.14em] uppercase text-slate-500 border-b border-slate-200">
                                    <th className="px-2 py-2 w-1/3">{t("estimator.country")}</th>
                                    <th className="px-2 py-2 w-1/3">{t("estimator.sales")}</th>
                                    <th className="px-2 py-2">{t("estimator.vatRate")}</th>
                                    <th className="px-2 py-2 text-right">{t("estimator.vatOwed")}</th>
                                    <th className="px-2 py-2 w-8" />
                                </tr>
                            </thead>
                            <tbody>
                                {computed.map((r, idx) => (
                                    <tr key={r.id} data-testid={`estimator-row-${idx}`} className="border-b border-slate-100">
                                        <td className="px-2 py-2.5">
                                            <Select value={r.country.code} onValueChange={(v) => update(r.id, { country: v })}>
                                                <SelectTrigger data-testid={`row-${idx}-country`} className="h-10 border-slate-200">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent className="max-h-72">
                                                    {EU_COUNTRIES.map((c) => (
                                                        <SelectItem key={c.code} value={c.code}>
                                                            <span className="mr-2">{c.flag}</span> {c.name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </td>
                                        <td className="px-2 py-2.5">
                                            <div className="relative">
                                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">€</span>
                                                <Input
                                                    data-testid={`row-${idx}-amount`}
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    value={r.amount}
                                                    onChange={(e) => update(r.id, { amount: e.target.value })}
                                                    className="h-10 pl-6 border-slate-200 focus-visible:ring-[#10B981]"
                                                    placeholder="0.00"
                                                />
                                            </div>
                                        </td>
                                        <td className="px-2 py-2.5 text-slate-700 font-mono">{fmtPct(r.rate)}</td>
                                        <td className="px-2 py-2.5 text-right font-display font-bold text-slate-900">
                                            {fmtEUR(r.vat)}
                                        </td>
                                        <td className="px-2 py-2.5">
                                            <button
                                                data-testid={`row-${idx}-remove`}
                                                onClick={() => remove(r.id)}
                                                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-rose-500 transition-colors"
                                                aria-label={t("estimator.removeRow")}
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <Stat label={t("estimator.totalSales")} value={fmtEUR(totalSales)} testId="total-sales" />
                        <Stat label={t("estimator.totalVat")} value={fmtEUR(totalVat)} highlight testId="total-vat" />
                        <div data-testid="oss-file-badge" className="rounded-md border border-emerald-200 bg-emerald-50 p-4 flex items-center gap-2.5">
                            <BadgeCheck className="h-5 w-5 text-[#10B981]" />
                            <div>
                                <div className="text-xs font-semibold tracking-wider uppercase text-emerald-700">
                                    {t("estimator.fileViaOss")}
                                </div>
                                <div className="text-sm text-slate-700 leading-tight mt-0.5">
                                    {t("estimator.ossSubtitle")}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function Stat({ label, value, highlight, testId }) {
    return (
        <div
            data-testid={testId}
            className={`rounded-md border p-4 ${
                highlight ? "border-[#10B981]/40 bg-emerald-50/50" : "border-slate-200 bg-slate-50/60"
            }`}
        >
            <div className="text-xs font-semibold tracking-[0.14em] uppercase text-slate-500">{label}</div>
            <div className={`mt-1 font-display text-2xl font-black ${highlight ? "text-[#10B981]" : "text-slate-900"}`}>
                {value}
            </div>
        </div>
    );
}
